import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { logger } from '@/lib/logger';

import { SERVICE_ACCOUNT_ENTITY_TYPE } from './api-access-credentials';

export async function deleteCredential(
  orgId: number,
  userId: number,
  principalEntityId: string,
  lastEventSeq: number,
  entityTypeName: string = SERVICE_ACCOUNT_ENTITY_TYPE
): Promise<void> {
  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
    startSeq: lastEventSeq,
  });
  builder.objectDeleted(principalEntityId, entityTypeName);
  await createEventsClient(orgId).submitEvents(builder.build());
}

export interface AttachedRowRef {
  entityId: string;
  principalEntityId: string;
  lastEventSeq: number;
}

/**
 * Deletes multiple ahoy_secret rows attached to a credential, in a single
 * batched events submission. Each row gets its own EventBuilder instance --
 * every row has independently-evolved `last_event_seq`, and a single shared
 * builder/startSeq cannot correctly compute seq for more than one entity (see
 * EventBuilder's per-entity defaultStartSeq behavior).
 */
export async function deleteAttachedRows(
  orgId: number,
  userId: number,
  rows: AttachedRowRef[]
): Promise<void> {
  const allEvents = rows.flatMap(
    ({ entityId, principalEntityId, lastEventSeq }) => {
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(userId),
        startSeq: lastEventSeq,
      });
      builder
        .associationRemoved(
          entityId,
          'ahoy_secret',
          'ahoy_secret_to_principal',
          principalEntityId
        )
        .objectDeleted(entityId, 'ahoy_secret');
      return builder.build();
    }
  );

  logger.info('Deleting attached rows in a batched submission', {
    component: 'crm_events',
    org_id: orgId,
    row_count: rows.length,
    row_entity_ids: rows.map(row => row.entityId),
  });

  await createEventsClient(orgId).submitEvents(allEvents);
}
